import React, { useEffect } from "react";
// Import removed - now using @dnd-kit
import {
	ExtendedFormTemplate,
	FormSection,
	FormField,
	FieldType,
} from "../types/formTypes";

import SectionTitle from "./form-builder/SectionTitle";
import SectionFields from "./form-builder/SectionFields";
import { createNewField, duplicateField } from "../utils/fieldUtils";
import { Button } from "../../../components/ui/button";
import { Plus } from "lucide-react";

interface FormBuilderCanvasProps {
	formTemplate: ExtendedFormTemplate;
	activeSection: string;
	onUpdateSection: (sectionId: string, updates: Partial<FormSection>) => void;
}

const FormBuilderCanvas: React.FC<FormBuilderCanvasProps> = ({
	formTemplate,
	activeSection,
	onUpdateSection,
}) => {
	useEffect(() => {
		const handleOdontogramFieldsUpdate = (event: CustomEvent) => {
			if (!event.detail) return;

			const { fieldId, odontogramFields } = event.detail;

			formTemplate.definition.sections.forEach(section => {
				const fieldIndex = section.fields.findIndex(field => field.id === fieldId);

				if (fieldIndex >= 0) {
					const updatedFields = [...section.fields];
					updatedFields[fieldIndex] = {
						...updatedFields[fieldIndex],
						odontogramFields
					};

					onUpdateSection(section.id, {
						fields: updatedFields
					});
				}
			});
		};

		const handleAppointmentFieldsUpdate = (event: CustomEvent) => {
			if (!event.detail) return;

			const { fieldId, appointmentFields, maxAppointments } = event.detail;

			formTemplate.definition.sections.forEach(section => {
				const fieldIndex = section.fields.findIndex(field => field.id === fieldId);

				if (fieldIndex >= 0) {
					const updatedFields = [...section.fields];
					const updatedField: any = {
						...updatedFields[fieldIndex],
						appointmentFields,
					};

					if ('maxAppointments' in event.detail) {
						if (maxAppointments === undefined) {
							delete updatedField.maxAppointments;
						} else {
							updatedField.maxAppointments = maxAppointments;
						}
					}

					updatedFields[fieldIndex] = updatedField;

					onUpdateSection(section.id, {
						fields: updatedFields
					});
				}
			});
		};

		document.addEventListener('odontogramFieldsUpdate', handleOdontogramFieldsUpdate as EventListener);
		document.addEventListener('appointmentFieldsUpdate', handleAppointmentFieldsUpdate as EventListener);

		return () => {
			document.removeEventListener('odontogramFieldsUpdate', handleOdontogramFieldsUpdate as EventListener);
			document.removeEventListener('appointmentFieldsUpdate', handleAppointmentFieldsUpdate as EventListener);
		};
	}, [formTemplate, onUpdateSection]);

	const currentSection = formTemplate.definition.sections.find(
		(section) => section.id === activeSection
	);

	if (!currentSection) {
		return (
			<div className="flex-1 p-6">
				Abschnitt auswählen
			</div>
		);
	}

	const handleSectionUpdate = (key: keyof FormSection, value: string) => {
		onUpdateSection(activeSection, { [key]: value });
	};

	const addFieldAt = (index: number, type: FieldType = 'text') => {
		const newField = createNewField(type);
		const newFields = [...currentSection.fields];
		newFields.splice(index, 0, newField);

		onUpdateSection(activeSection, {
			fields: newFields,
		});
	};

	const updateField = (fieldId: string, updates: Partial<FormField>) => {
		onUpdateSection(activeSection, {
			fields: currentSection.fields.map((field) =>
				field.id === fieldId ? { ...field, ...updates } : field
			),
		});
	};

	const deleteField = (fieldId: string) => {
		onUpdateSection(activeSection, {
			fields: currentSection.fields.filter(
				(field) => field.id !== fieldId
			),
		});
	};

	const handleDuplicateField = (fieldId: string) => {
		const fieldToDuplicate = currentSection.fields.find(
			(field) => field.id === fieldId
		);
		if (!fieldToDuplicate) return;

		const newField = duplicateField(fieldToDuplicate);
		const fieldIndex = currentSection.fields.findIndex(
			(field) => field.id === fieldId
		);

		// Insert the duplicated field right after the original
		const newFields = [...currentSection.fields];
		newFields.splice(fieldIndex + 1, 0, newField);

		onUpdateSection(activeSection, {
			fields: newFields,
		});
	};

	const handleDragEnd = (result: { fromIndex: number; toIndex: number }) => {
		const { fromIndex, toIndex } = result;

		const fields = Array.from(currentSection.fields);
		const [removed] = fields.splice(fromIndex, 1);
		fields.splice(toIndex, 0, removed);

		onUpdateSection(activeSection, { fields });
	};

	return (
		<div className="flex-1">
			<SectionTitle
				title={currentSection.title}
				onChange={(title) => handleSectionUpdate("title", title)}
			/>
			<SectionFields
				section={currentSection}
				onUpdateField={updateField}
				onDeleteField={deleteField}
				onDuplicateField={handleDuplicateField}
				onDragEnd={handleDragEnd}
				onAddFieldAt={addFieldAt}
			/>
			<div className="mt-6 flex gap-3">
				<Button
					onClick={() => addFieldAt(currentSection.fields.length, 'text')}
					variant="outline"
					className="flex-1"
					aria-label="Feld hinzufügen"
					data-testid="add-field-button"
				>
					<Plus className="h-4 w-4 mr-2" />
					Feld hinzufügen
				</Button>
				<Button
					onClick={() => addFieldAt(currentSection.fields.length, 'title')}
					variant="outline"
					className="flex-1"
					aria-label="Titel hinzufügen"
					data-testid="add-title-button"
				>
					<Plus className="h-4 w-4 mr-2" />
					Titel
				</Button>
			</div>
		</div>
	);
};

export default FormBuilderCanvas;
